import { PROVIDER_ERROR_CODE } from '@steerfi/shared';
import { config } from '@/config';
import { getAuthToken } from './keycloak-auth';

const API_BASE_URL = '/internal/providers';

/**
 * Serialises list filters into a query string.
 *
 * Arrays become **repeated parameters** — `?role=4&role=5` — because that is what
 * this backend expects: Express/qs turns repeated keys into an array, and
 * `queryProviderSchema` validates each element against its enum.
 *
 * ⚠️ Deliberately NOT comma-joined. `api/suppliers.ts` joins with commas and is
 * right to, because `supplier.dto.ts` splits on commas on the way in. The
 * providers endpoint does not, so `?status=active,inactive` arrives as a single
 * string, fails the enum, and returns 400. That failure renders an empty table
 * rather than an error, which is easy to misread as missing data.
 *
 * An empty array contributes nothing, matching how `undefined` and `''` are
 * skipped: "nothing selected" must not become `?role=`.
 */
const buildQuery = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });
  const s = searchParams.toString();
  return s ? `?${s}` : '';
};

export class ProviderApiError extends Error {
  status: number;
  body?: Record<string, unknown>;

  constructor(message: string, status: number, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ProviderApiError';
    this.status = status;
    if (body !== undefined) {
      this.body = body;
    }
  }
}

async function providersRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${config.api.baseURL}${API_BASE_URL}${path}`;
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const mergedHeaders: Record<string, string> = { ...headers };
  if (
    options.headers &&
    typeof options.headers === 'object' &&
    !(options.headers instanceof Headers)
  ) {
    Object.assign(mergedHeaders, options.headers as Record<string, string>);
  }

  const response = await fetch(url, {
    ...options,
    headers: mergedHeaders,
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    const msg =
      (typeof body.message === 'string' && body.message) ||
      (typeof body.error === 'string' && body.error) ||
      `HTTP ${response.status}`;
    throw new ProviderApiError(msg, response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  const parsed = JSON.parse(text) as Record<string, unknown>;
  if (
    parsed &&
    typeof parsed === 'object' &&
    'success' in parsed &&
    'data' in parsed &&
    !('pagination' in parsed)
  ) {
    return (parsed as { data: T }).data;
  }
  return parsed as T;
}

export type ProviderStatusFilter = 'active' | 'inactive';

export interface ProviderListQuery {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  /**
   * Omit entirely for "no status filter" — there is no sentinel value. The
   * backend enum accepts only `active` and `inactive`, so anything else returns
   * 400. The page uses `'all'` as its own dropdown sentinel and converts it to
   * `undefined` before it reaches here.
   */
  status?: ProviderStatusFilter | ProviderStatusFilter[];
  /**
   * Party role ids from `PARTY_ROLE`. **Every** role is accepted: a provider can be
   * created with any of them, because provider-ness is `party_type_id` rather than
   * `party_role_id`. Serialised as repeated params (`?role=4&role=5`); a
   * comma-joined value, or an id that is not in `PARTY_ROLE`, returns a 400.
   *
   * Deliberately not enumerated here. This comment previously listed four ids and
   * went stale the moment the set widened, so it points at the constant instead.
   */
  role?: number | number[];
}

export interface ProviderApiRow {
  party_id: string;
  provider_name: string;
  provider_code: string;
  license_number: string | null;
  party_role_id: number;
  status_id: number;
  financial_details: unknown;
  metadata: unknown;
  created_at: string;
  updated_at: string;
  display_name: string | null;
  client_code: string | null;
}

export interface ProviderListResponse {
  data: ProviderApiRow[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface CreateProviderDto {
  provider_name: string;
  provider_code: string;
  license_number?: string | null;
  party_role_id: number;
  party_status_id: number;
}

export interface UpdateProviderDto {
  provider_name?: string;
  provider_code?: string;
  license_number?: string | null;
  party_role_id?: number;
  party_status_id?: number;
}

export type DeleteProviderResult =
  | { ok: true }
  | {
      ok: false;
      code: typeof PROVIDER_ERROR_CODE.HAS_ACTIVE_RELATIONSHIPS;
      activeRelationshipCount: number;
    }
  | { ok: false; code: 'OTHER'; message: string };

export const listProviders = async (
  query: ProviderListQuery = {}
): Promise<{ success: boolean; data: ProviderListResponse }> => {
  const data = await providersRequest<ProviderListResponse>(
    `${buildQuery(query as Record<string, unknown>)}`
  );
  return { success: true, data };
};

export const getProvider = async (
  partyId: string
): Promise<{ success: boolean; data: ProviderApiRow }> => {
  const data = await providersRequest<ProviderApiRow>(`/${partyId}`);
  return { success: true, data };
};

export const createProvider = async (
  data: CreateProviderDto
): Promise<{ success: boolean; data: ProviderApiRow }> => {
  const row = await providersRequest<ProviderApiRow>('', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return { success: true, data: row };
};

export const updateProvider = async (
  partyId: string,
  data: UpdateProviderDto
): Promise<{ success: boolean; data: ProviderApiRow }> => {
  const row = await providersRequest<ProviderApiRow>(`/${partyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return { success: true, data: row };
};

export const deleteProvider = async (partyId: string): Promise<DeleteProviderResult> => {
  try {
    await providersRequest<void>(`/${partyId}`, { method: 'DELETE' });
    return { ok: true };
  } catch (err: unknown) {
    if (
      err instanceof ProviderApiError &&
      err.status === 409 &&
      err.body?.code === PROVIDER_ERROR_CODE.HAS_ACTIVE_RELATIONSHIPS
    ) {
      return {
        ok: false,
        code: PROVIDER_ERROR_CODE.HAS_ACTIVE_RELATIONSHIPS,
        activeRelationshipCount: Number(
          (err.body?.details as Record<string, unknown> | undefined)?.activeRelationshipCount ?? 0
        ),
      };
    }
    return {
      ok: false,
      code: 'OTHER',
      message: err instanceof Error ? err.message : 'Failed to delete provider',
    };
  }
};
